'use client';

import type { InitOptions } from 'i18next';
import type { LangCode } from './locales-config';

import i18next from 'i18next';
import { useEffect } from 'react';
import { getStorage } from 'minimal-shared/utils';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next, I18nextProvider as Provider } from 'react-i18next';

import { CONFIG } from 'src/global-config';

import { i18nOptions, fallbackLng, supportedLngs, storageConfig, i18nResourceLoader } from './locales-config';

// ----------------------------------------------------------------------

/**
 * Reads the locale claim from a JWT token stored in sessionStorage.
 * Returns 'es' (default) if the token is absent, malformed, or the
 * locale claim is not one of the supported languages.
 */
function getLocaleFromJWT(): LangCode {
  try {
    const JWT_STORAGE_KEY = 'accessToken'; // mirrors src/auth/context/jwt/constant.ts
    const token = sessionStorage.getItem(JWT_STORAGE_KEY);
    if (!token) return fallbackLng;

    const parts = token.split('.');
    if (parts.length < 2) return fallbackLng;

    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    const locale = payload?.locale as string | undefined;

    if (locale && (supportedLngs as readonly string[]).includes(locale)) {
      return locale as LangCode;
    }
  } catch {
    // silent — fall through to default
  }
  return fallbackLng;
}

// ----------------------------------------------------------------------

let i18nextLng: LangCode;

if (CONFIG.isStaticExport) {
  i18nextLng = (getStorage(
    storageConfig.localStorage.key,
    storageConfig.localStorage.autoDetection ? undefined : fallbackLng
  ) as LangCode) ?? fallbackLng;
} else {
  // Priority: JWT locale claim > cookie/localStorage > default ('es')
  i18nextLng = getLocaleFromJWT();
}

/**
 * Initialize i18next
 */
const initOptions: InitOptions = CONFIG.isStaticExport
  ? { ...i18nOptions(i18nextLng), detection: { caches: ['localStorage'] } }
  : { ...i18nOptions(i18nextLng), detection: { caches: ['cookie'] } };

i18next.use(LanguageDetector).use(initReactI18next).use(i18nResourceLoader).init(initOptions);

// ----------------------------------------------------------------------

export { i18next };

// ----------------------------------------------------------------------

type I18nProviderProps = {
  lang?: LangCode;
  children: React.ReactNode;
};

export function I18nProvider({ lang, children }: I18nProviderProps) {
  /**
   * Apply language from JWT claim or explicit override.
   * This runs on mount and whenever `lang` changes (e.g. after login).
   */
  useEffect(() => {
    const targetLang = lang ?? getLocaleFromJWT();
    if (targetLang && i18next.language !== targetLang) {
      i18next.changeLanguage(targetLang);
    }
  }, [lang]);

  return <Provider i18n={i18next}>{children}</Provider>;
}
